/** Mirror of the F9 section of `backend/types/technicians.ts` (CRA cannot import backend/).
 * `backend/tests/technicianImportContract.test.ts` fails when the two lists drift. */

export const CONTRACT_TYPES = ['contractor', 'fixed_term'] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];
export const PROFILE_KINDS = ['installer', 'merch'] as const;
export type ProfileKind = (typeof PROFILE_KINDS)[number];
export const EXPERIENCE_LEVELS = ['junior', 'intermediate', 'senior', 'expert'] as const;
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number];
export const VEHICLE_TYPES = ['none', 'car', 'small_van', 'van', 'truck'] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];
export const EXCLUSION_REASONS = ['no_show', 'behaviour', 'end_of_collab', 'stopped_activity', 'expenses', 'insufficient_level', 'other'] as const;
export type ExclusionReason = (typeof EXCLUSION_REASONS)[number];
export const TEAM_SIZE_RANGE = { min: 1, max: 50 } as const;
export const OPS_NOTE_MAX_LENGTH = 2000;

/** The F9 profile fields (F30), added to `TechnicianRecord.profile` by the upsert PR. */
export interface TechnicianProfileFields {
  contractType: ContractType | null;
  profileKind: ProfileKind | null;
  experienceLevel: ExperienceLevel | null;
  teamSize: number | null;
  vehicleType: VehicleType | null;
}

export interface UserRef {
  id: number;
  name: string;
}

/** Null on the record when not excluded; never exposed to the technician himself (F29). */
export interface TechnicianExclusion {
  excludedAt: string;
  reason: ExclusionReason;
  excludedBy: UserRef | null;
}

/** Null on the record when no note was ever written. */
export interface OpsNote {
  text: string;
  updatedAt: string;
  updatedBy: UserRef | null;
}

// ---- F9 import contract (PLAN §API). ----

/** Stable column codes carried by `_meta`, in template order (PLAN §Gabarit). One
 * `specialty:<code>` column per active specialty sits between `zones_secondary` and `expertises`. */
export const TECHNICIAN_IMPORT_COLUMNS = [
  'id', 'first_name', 'last_name', 'email', 'phone', 'contract_type', 'company',
  'profile_kind', 'experience_level', 'team_size', 'vehicle_type', 'lang', 'is_active',
  'service_desk_id', 'allowed_ticket_creation_desks',
  'zones_primary', 'zones_secondary', 'expertises',
  'warehouse_address', 'warehouse_postal_code', 'warehouse_city', 'warehouse_area_sqm',
  'delivery_address', 'delivery_postal_code', 'delivery_city', 'delivery_area_sqm',
  'billing_address', 'billing_postal_code', 'billing_city',
  'excluded_reason', 'excluded_at', 'ops_note',
] as const;
export const SPECIALTY_COLUMN_PREFIX = 'specialty:';
export type TechnicianImportField = (typeof TECHNICIAN_IMPORT_COLUMNS)[number] | `specialty:${string}`;

/** A whole-file refusal: 400 with this code, nothing analysed (CA5). */
export const IMPORT_FILE_ERROR_CODES = [
  'not_xlsx', 'too_large', 'too_many_rows', 'country_mismatch', 'missing_column', 'unknown_version', 'no_rows', 'zip_bomb',
] as const;
export type ImportFileErrorCode = (typeof IMPORT_FILE_ERROR_CODES)[number];

/** Why one row is set aside; `login_taken` and `modified_meanwhile` only surface at apply time. */
export const IMPORT_ROW_ERROR_REASONS = [
  'required', 'invalid_format', 'unknown_referential', 'ambiguous_label', 'zone_overlap', 'secondary_without_primary',
  'duplicate_email', 'duplicate_id', 'duplicate_login',
  'email_not_technician', 'email_other_country', 'id_unknown', 'id_not_technician', 'id_other_country', 'id_email_mismatch',
  'login_taken', 'modified_meanwhile', 'unexpected', 'address_not_geocodable',
  'unknown_desk',
] as const;
export type ImportRowErrorReason = (typeof IMPORT_ROW_ERROR_REASONS)[number];

export interface ImportFileError {
  code: ImportFileErrorCode;
  /** The missing column code for `missing_column`. */
  column?: string;
}

/** `row` is the Excel row number; `cols` every Excel column letter concerned, `col` the first or null.
 * A `warning` never sets the row aside; `address_not_geocodable` names the address group in `field`. */
export interface ImportRowError {
  row: number;
  name: string | null;
  col: string | null;
  cols: string[];
  field: TechnicianImportField | null;
  value: string | null;
  reason: ImportRowErrorReason;
  severity: 'error' | 'warning';
}

export interface ImportAddition {
  row: number;
  name: string;
  email: string;
  company: string | null;
  zonesPrimary: string[];
}

/** Multi-valued fields carry sorted codes joined by `, ` (desks: sorted ids, or `*`); `erased` = a filled value becomes empty. */
export interface ImportFieldChange {
  field: TechnicianImportField;
  from: string | null;
  to: string | null;
  erased: boolean;
}

export interface ImportChange {
  row: number;
  userId: number;
  name: string;
  fields: ImportFieldChange[];
}

/** `userId` is null for `notCreated`: a new row carrying a reason is never created (CA10). */
export interface ImportExclusion {
  row: number;
  userId: number | null;
  name: string;
  reason: ExclusionReason | null;
}

export interface TechnicianImportValidateResponse {
  fingerprint: string;
  rowsRead: number;
  emptyRows: number;
  /** False when addresses were not checked in this response (no provider, outage, bounds): no warning emitted. */
  geocodingChecked: boolean;
  counts: {
    /** Error-severity entries only; warnings are counted apart. */
    errors: number;
    warnings: number;
    additions: number;
    changes: number;
    toExclude: number;
    toReinstate: number;
    notCreated: number;
    unchanged: number;
    absent: number;
  };
  errors: ImportRowError[];
  additions: ImportAddition[];
  changes: ImportChange[];
  exclusions: { toExclude: ImportExclusion[]; toReinstate: ImportExclusion[]; notCreated: ImportExclusion[] };
  unchanged: number;
  absent: Array<{ userId: number; name: string }>;
}

export interface TechnicianImportApplyResponse {
  importedAt: string;
  counts: { created: number; updated: number; excluded: number; reinstated: number; failed: number };
  failed: Array<{ row: number; reason: ImportRowErrorReason }>;
}

/** 409 on apply: the base moved since the preview; nothing was written (CA14). */
export interface TechnicianImportConflictResponse {
  code: 'preview_outdated';
  preview: TechnicianImportValidateResponse;
}
